import * as amplitude from '@amplitude/analytics-browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { createDiscussion } from 'hb-react/frontoffice/apps/Forum/requests';
import { type DiscussionEditValues } from 'hb-react/frontoffice/apps/Forum/requests/requests.types';
import { type Discussion } from 'hb-react/frontoffice/apps/Forum/types';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';

export const useCreateDiscussion = () => {
  const { appPageConfig, configUrls } = useForumContext();
  const { topicId } = useTopicContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isCategoryFieldMandatory =
    appPageConfig?.config?.set_category_field_mandatory;

  const { isPending, mutateAsync: mutate } = useMutation({
    mutationFn: (values: DiscussionEditValues) =>
      createDiscussion({ configUrls, values }),

    onSuccess: (
      discussion: Partial<Discussion>,
      variables: DiscussionEditValues,
    ) => {
      toast.success(__('Successfully created'));
      queryClient.invalidateQueries({
        queryKey: [ITEMS_QUERY_KEYS.forum.items, topicId],
      });
      amplitude.track('Forums|Creation', { group_id: topicId });
      if (!isCategoryFieldMandatory) {
        amplitude.track('Forums|CreationCategories', {
          count: variables.category_ids.length,
          group_id: topicId,
        });
      }
      if (discussion.id) {
        navigate(configUrls.discussionUrl(discussion.id));
      }
    },
    onError: () => {
      toast.error(__('Sorry, an error occurred'));
    },
  });

  return {
    isPending,
    mutate: (values: DiscussionEditValues) => mutate(values),
  };
};
